import { NavigatorInfo, PulseEnvironment, PulseNavigator } from '@pulse/browser-shared';
import { HardwareInfo, ScreenInfo } from '../../models';

export class DeviceInfoService {
    constructor(private env: PulseEnvironment) {}

    getHardwareInfo(): HardwareInfo {
        const { navigator } = this.env.win;
        const gpu = this.getGPU();

        const hardware: HardwareInfo = {
            device_memory: navigator.deviceMemory || undefined,
            cpus: navigator.hardwareConcurrency || undefined,
            gpu_vendor: gpu?.vendor,
            gpu_renderer: gpu?.renderer,
        };

        return hardware;
    }

    getScreenInfo(): ScreenInfo {
        const win = this.env.win;
        const screen = win.screen;

        const touch =
            win.navigator.maxTouchPoints > 0 ||
            (win.navigator as any).msMaxTouchPoints > 0 ||
            'ontouchstart' in navigator;

        return {
            touch,
            resolution_x: screen.width,
            resolution_y: screen.height,
            available_resolution_x: screen.availWidth,
            available_resolution_y: screen.availHeight,
            pixel_ratio: win.devicePixelRatio,
            pixel_depth: screen.pixelDepth,
        };
    }

    getNavigatorInfo(): NavigatorInfo {
        const nav = this.env.win.navigator;
        let lang = nav.language || nav.languages[0] || 'unknown';
        lang = (lang.split('-').shift() ?? '').trim() || 'unknown';

        return {
            device_language_code: lang,
            browser_platform: nav.userAgentData?.platform ?? nav.platform ?? 'unknown',
            browser_vendor: this.getBrowserBrand(nav),
        };
    }

    getUserAgent(): string {
        return this.env.win.navigator.userAgent;
    }

    private getBrowserBrand(nav: PulseNavigator): string {
        return nav.userAgentData?.brands?.[0]?.brand || nav.userAgent || 'unknown';
    }

    private getGPU(): { vendor: string; renderer: string } | undefined {
        const win = this.env.win;
        const doc = win.document;
        const canvas = doc.createElement('canvas');

        try {
            const gl: WebGLRenderingContext | null = (canvas.getContext('webgl') ||
                canvas.getContext('experimental-webgl')) as WebGLRenderingContext;

            const debugInfo = gl?.getExtension('WEBGL_debug_renderer_info');

            if (!debugInfo) {
                return;
            }

            const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
            const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

            if (vendor || renderer) {
                return { vendor, renderer };
            }
        } catch (err) {
            console.warn(err);
        }

        return undefined;
    }
}
